import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  MessageCircle, X, Send, CheckCircle2, Circle, Search, Loader2,
  User as UserIcon, Tag, AlertCircle, ArrowLeft, Mail, Pencil, Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAGS = ['General', 'Keywords', 'Content', 'Backlinks', 'Traffic',
  'AI Visibility', 'Social Media', 'Paid Ads', 'AI Video'];

const tagColor = (t) => {
  const map = {
    General: 'bg-slate-700 text-slate-200',
    Keywords: 'bg-blue-900/60 text-blue-200',
    Content: 'bg-emerald-900/60 text-emerald-200',
    Backlinks: 'bg-sky-900/60 text-sky-200',
    Traffic: 'bg-cyan-900/60 text-cyan-200',
    'AI Visibility': 'bg-fuchsia-900/60 text-fuchsia-200',
    'Social Media': 'bg-pink-900/60 text-pink-200',
    'Paid Ads': 'bg-orange-900/60 text-orange-200',
    'AI Video': 'bg-violet-900/60 text-violet-200',
  };
  return map[t] || map.General;
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso); const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

// ────────────────────────────────────────────────────────────────
// Chat Thread
// ────────────────────────────────────────────────────────────────
function ChatThread({ conversation, user, onBack, onSent, onResolve }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [tag, setTag] = useState('General');
  const [sending, setSending] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [notifying, setNotifying] = useState(false);
  const [notifyMsg, setNotifyMsg] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [msgBusy, setMsgBusy] = useState(false);
  const endRef = useRef(null);
  const isAdminOrTeam = user.role !== 'client';
  const isSuperAdmin = user.role === 'super_admin';

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages/conversations/${conversation.id}/messages`, { headers: authHeaders() });
      setMessages(res.data);
      // mark as read
      await axios.post(`${API}/messages/conversations/${conversation.id}/read`, {}, { headers: authHeaders() });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [conversation.id]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Load team members once for super_admin reassign dropdown
  useEffect(() => {
    if (!isSuperAdmin) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/team-members`, { headers: authHeaders() });
        setTeamMembers(res.data || []);
      } catch (err) { /* silent */ }
    })();
  }, [isSuperAdmin]);

  const reassign = async (teamMemberId) => {
    try {
      await axios.patch(`${API}/messages/conversations/${conversation.id}`,
        { assigned_team_member_id: teamMemberId || null }, { headers: authHeaders() });
      onResolve && onResolve();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages/conversations/${conversation.id}/messages`,
        { body: text.trim(), tag }, { headers: authHeaders() });
      setText('');
      setTag('General');
      await fetchMessages();
      onSent && onSent();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setSending(false); }
  };

  const notifyViaEmail = async () => {
    setNotifying(true);
    setNotifyMsg('');
    try {
      const res = await axios.post(`${API}/messages/conversations/${conversation.id}/notify`, {}, { headers: authHeaders() });
      setNotifyMsg(res.data?.success ? 'Email sent to client' : 'Could not send email');
      setTimeout(() => setNotifyMsg(''), 3000);
    } catch (err) {
      setNotifyMsg(err.response?.data?.detail || 'Could not send email');
      setTimeout(() => setNotifyMsg(''), 3000);
    } finally {
      setNotifying(false);
    }
  };

  const toggleResolve = async () => {
    const newStatus = conversation.status === 'resolved' ? 'open' : 'resolved';
    try {
      await axios.patch(`${API}/messages/conversations/${conversation.id}`,
        { status: newStatus }, { headers: authHeaders() });
      onResolve && onResolve();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const startEdit = (m) => { setEditingId(m.id); setEditText(m.body); };
  const cancelEdit = () => { setEditingId(null); setEditText(''); };
  const saveEdit = async (id) => {
    if (!editText.trim()) return;
    setMsgBusy(true);
    try {
      await axios.put(`${API}/messages/${id}`, { body: editText.trim() }, { headers: authHeaders() });
      cancelEdit();
      await fetchMessages();
      onSent && onSent();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setMsgBusy(false); }
  };
  const deleteMessage = async (id) => {
    if (!window.confirm('Delete this message? This cannot be undone.')) return;
    setMsgBusy(true);
    try {
      await axios.delete(`${API}/messages/${id}`, { headers: authHeaders() });
      await fetchMessages();
      onSent && onSent();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setMsgBusy(false); }
  };

  const clientLabel = conversation.firm_name || conversation.client_email || 'Client';

  return (
    <div className="flex-1 flex flex-col bg-slate-950 h-full min-w-0">
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          {onBack && (
            <button onClick={onBack} className="p-1.5 hover:bg-white/5 rounded-lg text-slate-400" data-testid="chat-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
            <UserIcon className="w-4 h-4 text-white" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white truncate">
              {user.role === 'client' ? (conversation.assigned_team_member_email || 'Account Manager') : clientLabel}
            </div>
            <div className="text-[11px] text-slate-500 truncate">
              {user.role === 'client'
                ? (conversation.assigned_team_member_email ? 'Your account manager' : 'No account manager assigned')
                : conversation.assigned_team_member_email || 'Unassigned'}
            </div>
          </div>
        </div>
        {isAdminOrTeam && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {isSuperAdmin && (
              <select
                value={conversation.assigned_team_member_id || ''}
                onChange={(e) => reassign(e.target.value)}
                className="text-xs bg-slate-800 text-slate-200 border border-white/5 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-violet-500/30 max-w-[160px]"
                data-testid="chat-reassign-select"
                title="Reassign account manager"
              >
                <option value="">- Unassigned -</option>
                {teamMembers.map(m => (
                  <option key={m.id} value={m.id}>{m.email}</option>
                ))}
              </select>
            )}
            <button onClick={toggleResolve} className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
              conversation.status === 'resolved' ? 'bg-emerald-900/60 text-emerald-200 hover:bg-emerald-900/80'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`} data-testid="chat-resolve-btn">
              {conversation.status === 'resolved' ? 'Reopen' : 'Mark Resolved'}
            </button>
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
        {loading ? (
          <div className="flex items-center justify-center h-full text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />Loading messages...
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-slate-500 text-sm py-8">No messages yet. Start the conversation below.</div>
        ) : (
          messages.map((m) => {
            const mine = m.sender_role === user.role && m.sender_email === user.email;
            return (
              <div key={m.id} className={`group flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.id}`}>
                <div className={`max-w-[80%] ${mine ? 'bg-gradient-to-br from-violet-600 to-indigo-600 text-white' : 'bg-slate-800 text-slate-100'} rounded-2xl px-3.5 py-2.5 shadow-sm`}>
                  {!mine && (
                    <div className="text-[11px] font-medium mb-1 opacity-80 flex items-center gap-1.5">
                      <span>{m.sender_email}</span>
                      <span className="opacity-60">·</span>
                      <span className="capitalize opacity-60">{(m.sender_role || '').replace('_', ' ')}</span>
                    </div>
                  )}
                  {editingId === m.id ? (
                    <div className="flex flex-col gap-2 min-w-[180px]">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        rows={2}
                        className="text-sm text-slate-900 bg-white rounded-lg px-2.5 py-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-white/40"
                        data-testid={`msg-edit-input-${m.id}`}
                      />
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={cancelEdit} className="text-[11px] px-2 py-1 rounded-md bg-white/15 hover:bg-white/25 text-white" data-testid={`msg-edit-cancel-${m.id}`}>Cancel</button>
                        <button onClick={() => saveEdit(m.id)} disabled={msgBusy} className="text-[11px] px-2 py-1 rounded-md bg-white text-violet-700 font-semibold hover:bg-white/90 disabled:opacity-50" data-testid={`msg-edit-save-${m.id}`}>Save</button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm whitespace-pre-wrap break-words leading-relaxed">{m.body}</div>
                  )}
                  <div className="flex items-center justify-between mt-1.5 gap-2">
                    {m.tag && m.tag !== 'General' && (
                      <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium ${tagColor(m.tag)}`}>
                        <Tag className="w-2.5 h-2.5" />{m.tag}
                      </span>
                    )}
                    <span className={`text-[10px] ${mine ? 'text-white/60' : 'text-slate-500'} ml-auto flex items-center gap-1`}>
                      {m.edited_at && <span className="italic opacity-80">edited</span>}
                      {timeAgo(m.created_at)}
                    </span>
                    {mine && editingId !== m.id && (
                      <span className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => startEdit(m)} title="Edit" className="p-0.5 rounded hover:bg-white/20 text-white/80" data-testid={`msg-edit-btn-${m.id}`}>
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button onClick={() => deleteMessage(m.id)} title="Delete" className="p-0.5 rounded hover:bg-white/20 text-white/80" data-testid={`msg-delete-btn-${m.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div className="border-t border-white/5 p-3 bg-slate-900/80 flex-shrink-0">
        <div className="flex items-center gap-2 mb-2">
          <Tag className="w-3.5 h-3.5 text-slate-500" />
          <select value={tag} onChange={(e) => setTag(e.target.value)}
            className="text-xs bg-slate-800 text-slate-200 border border-white/5 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
            data-testid="chat-tag-select">
            {TAGS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          {isAdminOrTeam && (
            <div className="ml-auto flex items-center gap-2">
              {notifyMsg && <span className="text-[11px] text-emerald-400" data-testid="chat-notify-status">{notifyMsg}</span>}
              <button
                onClick={notifyViaEmail}
                disabled={notifying}
                className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg font-medium bg-slate-800 text-slate-200 border border-white/5 hover:bg-slate-700 hover:text-white transition-colors disabled:opacity-50"
                data-testid="chat-notify-email-btn"
                title="Email the client that they have a new message"
              >
                {notifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                Notify via email
              </button>
            </div>
          )}
        </div>
        <div className="flex items-end gap-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={2}
            placeholder={user.role === 'client' ? 'Write a message to your account manager…' : 'Reply to client…'}
            className="flex-1 bg-slate-800 text-slate-100 placeholder-slate-500 border border-white/5 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 resize-none"
            data-testid="chat-input" />
          <button onClick={send} disabled={sending || !text.trim()}
            className="p-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:shadow-lg hover:shadow-violet-500/30 text-white rounded-xl disabled:opacity-40 transition-all"
            data-testid="chat-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Conversation List (Admin / Team)
// ────────────────────────────────────────────────────────────────
function ConversationList({ conversations, user, activeId, onSelect }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [unreadOnly, setUnreadOnly] = useState(false);

  const filtered = conversations.filter(c => {
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (unreadOnly && !(c.unread_admin > 0)) return false;
    if (search) {
      const q = search.toLowerCase();
      return (c.firm_name || '').toLowerCase().includes(q)
        || (c.client_email || '').toLowerCase().includes(q)
        || (c.assigned_team_member_email || '').toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className="w-full flex flex-col bg-slate-900 border-r border-white/5 h-full" data-testid="conversation-list">
      <div className="p-3 border-b border-white/5 flex-shrink-0 space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search conversations..."
            className="w-full bg-slate-800 text-slate-200 placeholder-slate-500 border border-white/5 rounded-lg pl-9 pr-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-violet-500/30"
            data-testid="conversation-search" />
        </div>
        <div className="flex items-center gap-1.5">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="flex-1 text-xs bg-slate-800 text-slate-200 border border-white/5 rounded-lg px-2 py-1.5 focus:outline-none"
            data-testid="filter-status">
            <option value="all">All status</option>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
          </select>
          <button onClick={() => setUnreadOnly(v => !v)}
            className={`text-xs px-2 py-1.5 rounded-lg font-medium border transition-colors ${
              unreadOnly ? 'bg-violet-600 border-violet-500 text-white' : 'bg-slate-800 border-white/5 text-slate-300 hover:bg-slate-700'
            }`}
            data-testid="filter-unread">
            Unread
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="text-center text-slate-500 text-xs py-8 px-4">No conversations match.</div>
        ) : filtered.map(c => {
          const isActive = c.id === activeId;
          const hasUnread = (c.unread_admin || 0) > 0;
          return (
            <button key={c.id} onClick={() => onSelect(c)}
              className={`w-full text-left px-3 py-3 border-b border-white/5 hover:bg-white/5 transition-colors ${
                isActive ? 'bg-violet-900/30 border-l-2 border-l-violet-500' : ''
              }`}
              data-testid={`conv-item-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className={`text-sm truncate ${hasUnread ? 'text-white font-semibold' : 'text-slate-200 font-medium'}`}>
                      {c.firm_name || c.client_email || 'Client'}
                    </span>
                    {c.status === 'resolved' && (
                      <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate mt-0.5">
                    {c.assigned_team_member_email || <span className="text-amber-400">Unassigned</span>}
                  </div>
                  <div className={`text-xs truncate mt-1 ${hasUnread ? 'text-slate-200' : 'text-slate-400'}`}>
                    {c.last_message || <span className="italic text-slate-500">No messages yet</span>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="text-[10px] text-slate-500">{timeAgo(c.last_message_at)}</span>
                  {hasUnread && (
                    <span className="min-w-[18px] h-[18px] bg-violet-500 text-white text-[10px] rounded-full flex items-center justify-center px-1 font-semibold" data-testid={`conv-unread-${c.id}`}>
                      {c.unread_admin}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Messages Panel (slide-in) + Floating Bubble
// ────────────────────────────────────────────────────────────────
export default function MessagesBubble({ user }) {
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeConv, setActiveConv] = useState(null);
  const [unread, setUnread] = useState(0);
  const isClient = user.role === 'client';
  const isAdminOrTeam = !isClient;

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages/conversations`, { headers: authHeaders() });
      setConversations(res.data);
      if (isClient && res.data.length && !activeConv) {
        setActiveConv(res.data[0]);
      } else if (activeConv) {
        const refreshed = res.data.find(c => c.id === activeConv.id);
        if (refreshed) setActiveConv(refreshed);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [isClient, activeConv]);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages/unread`, { headers: authHeaders() });
      setUnread(res.data.count || 0);
    } catch (err) { /* silent */ }
  }, []);

  // Initial + polling
  useEffect(() => {
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (open) {
      fetchConversations();
      const t = setInterval(fetchConversations, 15000);
      return () => clearInterval(t);
    }
  // eslint-disable-next-line
  }, [open]);

  const handleSent = () => { fetchConversations(); fetchUnread(); };
  const handleResolve = () => { fetchConversations(); };

  const showBack = isAdminOrTeam && activeConv; // mobile-style back to list

  return (
    <>
      {/* Floating bubble */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 group flex items-center gap-2.5 pl-4 pr-5 py-3 sm:pl-3 sm:pr-5 sm:py-3 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-2xl shadow-xl shadow-violet-600/40 hover:scale-[1.03] hover:shadow-2xl hover:shadow-violet-600/50 transition-all"
        data-testid="messages-bubble"
        aria-label="Chat with your account manager"
        title="Chat with your account manager"
      >
        <div className="relative w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
          <MessageCircle className="w-5 h-5 text-white" />
          {unread > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] bg-red-500 border-2 border-violet-600 text-white text-[10px] rounded-full flex items-center justify-center px-1 font-bold shadow-lg" data-testid="messages-unread-badge">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </div>
        <span className="hidden sm:inline text-sm font-semibold text-white whitespace-nowrap" data-testid="messages-bubble-label">
          Chat with Account Manager
        </span>
      </button>

      {/* Slide-in Panel */}
      {open && (
        <div className="fixed inset-0 z-50 flex" data-testid="messages-panel">
          {/* Backdrop */}
          <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          {/* Panel - sized so both conversation list + chat thread render fully on desktop */}
          <div
            className="w-full sm:w-[680px] md:w-[840px] lg:w-[960px] max-w-[100vw] bg-slate-950 border-l border-white/10 h-full flex flex-col shadow-2xl"
            style={{ animation: 'messagesSlideIn 280ms cubic-bezier(0.22, 1, 0.36, 1)' }}
          >
            {/* Top bar */}
            <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between bg-slate-900 flex-shrink-0">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-violet-400" />
                <span className="text-white font-semibold">Messages</span>
                {unread > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-violet-900/60 text-violet-200 font-medium">{unread} new</span>}
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/5 rounded-lg text-slate-400" data-testid="messages-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 flex min-h-0">
              {loading ? (
                <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />Loading...
                </div>
              ) : isClient ? (
                activeConv ? (
                  <ChatThread conversation={activeConv} user={user} onSent={handleSent} />
                ) : (
                  <div className="flex-1 flex items-center justify-center text-slate-500 text-sm p-6 text-center">
                    <div>
                      <AlertCircle className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                      <p>Unable to load your conversation.</p>
                    </div>
                  </div>
                )
              ) : (
                <>
                  {/* List - hide on mobile when conv open */}
                  <div className={`${activeConv ? 'hidden sm:flex' : 'flex'} h-full w-full sm:w-[280px] flex-shrink-0`}>
                    <ConversationList conversations={conversations} user={user} activeId={activeConv?.id} onSelect={setActiveConv} />
                  </div>
                  {/* Thread */}
                  <div className={`${activeConv ? 'flex' : 'hidden sm:flex'} flex-1 min-w-0 h-full`}>
                    {activeConv ? (
                      <ChatThread conversation={activeConv} user={user} onBack={showBack ? () => setActiveConv(null) : null} onSent={handleSent} onResolve={handleResolve} />
                    ) : (
                      <div className="flex-1 flex items-center justify-center text-slate-500 text-sm p-6 text-center bg-slate-950">
                        <div>
                          <MessageCircle className="w-10 h-10 text-slate-700 mx-auto mb-3" />
                          <p>Select a conversation to start replying.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes messagesSlideIn {
          from { transform: translateX(100%); opacity: 0.4; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </>
  );
}
